import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { WeightAttributionResponse, WeightAttributionItem, BacktestAttributionResponse, BacktestAttributionDay, UserSymphony } from '../types/api';
import { RefreshCw, Calendar, ArrowUpDown, Scale, Shuffle, FlaskConical, Radio, ChevronLeft, ChevronRight, ListFilter } from 'lucide-react';
import { CollapsibleCard } from '../components/CollapsibleCard';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const getDefaultStartDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
};

type TabMode = 'live' | 'backtest';
type SortField = 'symbol' | 'contribution_pct' | 'weight_change' | 'asset_return_pct' | 'holding_effect' | 'allocation_effect';
type SortDir = 'asc' | 'desc';

const formatPct = (val?: number | null) => {
  if (val === null || val === undefined) return '-';
  const sign = val >= 0 ? '+' : '';
  return `${sign}${val.toFixed(2)}%`;
};

const formatDollar = (val?: number | null) => {
  if (val === null || val === undefined) return '-';
  return `${val >= 0 ? '+' : '-'}$${Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const colorClass = (val?: number | null) => {
  if (val === null || val === undefined || val === 0) return 'text-gray-600';
  return val > 0 ? 'text-emerald-600' : 'text-red-600';
};

function AttributionTable({
  items,
  sortField,
  sortDir,
  onSort,
}: {
  items: WeightAttributionItem[];
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
}) {
  const sorted = [...items].sort((a, b) => {
    let aVal: number, bVal: number;
    switch (sortField) {
      case 'symbol':
        return sortDir === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol);
      case 'contribution_pct':
        aVal = Math.abs(a.contribution_pct ?? 0); bVal = Math.abs(b.contribution_pct ?? 0); break;
      case 'weight_change':
        aVal = Math.abs(a.weight_change); bVal = Math.abs(b.weight_change); break;
      case 'asset_return_pct':
        aVal = Math.abs(a.asset_return_pct ?? 0); bVal = Math.abs(b.asset_return_pct ?? 0); break;
      case 'holding_effect':
        aVal = Math.abs(a.holding_effect ?? 0); bVal = Math.abs(b.holding_effect ?? 0); break;
      case 'allocation_effect':
        aVal = Math.abs(a.allocation_effect ?? 0); bVal = Math.abs(b.allocation_effect ?? 0); break;
      default: aVal = 0; bVal = 0;
    }
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => (
    <th
      className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && <ArrowUpDown className="w-3 h-3 text-emerald-600" />}
      </span>
    </th>
  );

  return (
    <>
      <div className="hidden lg:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => onSort('symbol')}>
                  <span className="inline-flex items-center gap-1">Symbol {sortField === 'symbol' && <ArrowUpDown className="w-3 h-3 text-emerald-600" />}</span>
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Prev Wt %</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">New Wt %</th>
                <SortHeader field="weight_change" label="Wt Change" />
                <SortHeader field="asset_return_pct" label="Asset Return" />
                <SortHeader field="holding_effect" label="Holding" />
                <SortHeader field="allocation_effect" label="Allocation" />
                <SortHeader field="contribution_pct" label="Contribution" />
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Contrib $</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map((item) => (
                <tr key={item.symbol} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3"><span className="font-semibold text-gray-900">{item.symbol}</span></td>
                  <td className="px-3 py-3 text-right text-sm text-gray-600">{item.prev_weight.toFixed(2)}%</td>
                  <td className="px-3 py-3 text-right text-sm text-gray-600">{item.new_weight.toFixed(2)}%</td>
                  <td className={`px-3 py-3 text-right text-sm font-medium ${colorClass(item.weight_change)}`}>{item.weight_change > 0 ? '+' : ''}{item.weight_change.toFixed(2)}%</td>
                  <td className={`px-3 py-3 text-right text-sm font-medium ${colorClass(item.asset_return_pct)}`}>{formatPct(item.asset_return_pct)}</td>
                  <td className={`px-3 py-3 text-right text-sm ${colorClass(item.holding_effect)}`}>{formatPct(item.holding_effect)}</td>
                  <td className={`px-3 py-3 text-right text-sm ${colorClass(item.allocation_effect)}`}>{formatPct(item.allocation_effect)}</td>
                  <td className={`px-3 py-3 text-right text-sm font-semibold ${colorClass(item.contribution_pct)}`}>{formatPct(item.contribution_pct)}</td>
                  <td className={`px-3 py-3 text-right text-sm font-medium ${colorClass(item.contribution_dollars)}`}>{formatDollar(item.contribution_dollars)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="lg:hidden space-y-3">
        {sorted.map((item) => (
          <div key={item.symbol} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-gray-900 text-lg">{item.symbol}</span>
              <span className={`text-sm font-semibold ${colorClass(item.contribution_pct)}`}>{formatPct(item.contribution_pct)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-gray-500">Prev Wt:</span> <span className="text-gray-700">{item.prev_weight.toFixed(2)}%</span></div>
              <div><span className="text-gray-500">New Wt:</span> <span className="text-gray-700">{item.new_weight.toFixed(2)}%</span></div>
              <div><span className="text-gray-500">Change:</span> <span className={`font-medium ${colorClass(item.weight_change)}`}>{item.weight_change > 0 ? '+' : ''}{item.weight_change.toFixed(2)}%</span></div>
              <div><span className="text-gray-500">Return:</span> <span className={`font-medium ${colorClass(item.asset_return_pct)}`}>{formatPct(item.asset_return_pct)}</span></div>
              <div><span className="text-gray-500">Hold:</span> <span className={colorClass(item.holding_effect)}>{formatPct(item.holding_effect)}</span></div>
              <div><span className="text-gray-500">Alloc:</span> <span className={colorClass(item.allocation_effect)}>{formatPct(item.allocation_effect)}</span></div>
            </div>
            {item.contribution_dollars != null && (
              <div className={`mt-2 pt-2 border-t border-gray-100 text-sm font-medium ${colorClass(item.contribution_dollars)}`}>
                Contribution: {formatDollar(item.contribution_dollars)}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function SummaryCards({
  equityBefore,
  totalReturnPct,
  totalReturnDollars,
  holdingEffectTotal,
  allocationEffectTotal,
  assetCount,
}: {
  equityBefore?: number | null;
  totalReturnPct?: number | null;
  totalReturnDollars?: number | null;
  holdingEffectTotal?: number | null;
  allocationEffectTotal?: number | null;
  assetCount: number;
}) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Equity Before</p>
        <p className="text-lg font-bold text-gray-900 mt-1">
          {equityBefore ? `$${equityBefore.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-'}
        </p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Return</p>
        <p className={`text-lg font-bold mt-1 ${colorClass(totalReturnPct)}`}>{formatPct(totalReturnPct)}</p>
        <p className={`text-sm ${colorClass(totalReturnDollars)}`}>{formatDollar(totalReturnDollars)}</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center gap-1.5">
          <Scale className="w-3.5 h-3.5 text-blue-500" />
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Holding Effect</p>
        </div>
        <p className={`text-lg font-bold mt-1 ${colorClass(holdingEffectTotal)}`}>{formatPct(holdingEffectTotal)}</p>
        <p className="text-xs text-gray-400 mt-0.5">From existing weights</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center gap-1.5">
          <Shuffle className="w-3.5 h-3.5 text-purple-500" />
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Allocation Effect</p>
        </div>
        <p className={`text-lg font-bold mt-1 ${colorClass(allocationEffectTotal)}`}>{formatPct(allocationEffectTotal)}</p>
        <p className="text-xs text-gray-400 mt-0.5">From weight changes</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Assets</p>
        <p className="text-lg font-bold text-gray-900 mt-1">{assetCount}</p>
      </div>
    </div>
  );
}

/**
 * Loads LIVE attribution for a date — reads live positions from `GET /api/weight-attribution`.
 *
 * **This is bug 1, expressed as a type.** That route is Trade-context by decision (D.4.9/D.5.0):
 * it reads live Trade positions, not historical research output, and Lab's route table
 * deliberately excludes it (`app/api/lab_routes.py`, `TRADE_CONTEXT_ROUTES_NOT_SERVED_BY_LAB`).
 * The page nevertheless initialised in `live` mode and called it on mount, so a Lab visitor to
 * `/attribution` got `Not Found: /api/weight-attribution` before they had touched anything. The
 * frontend was stale relative to a backend ownership decision that had already been settled.
 *
 * The mode is now a property of the **product context that renders the page**, not a default
 * baked into the page: a caller that can serve live attribution supplies the loader, and a caller
 * that cannot omits it. Injecting the call rather than gating it with a boolean is what makes the
 * absence structural — `WeightAttribution.tsx` does not name `api.getWeightAttribution` at all, so
 * the route is not reachable from the Lab entry's module graph and
 * `scripts/measure_frontend_api_ownership.py` can prove it rather than trusting a flag.
 *
 * Trade's live attribution is untouched: the route still exists, `api.getWeightAttribution` still
 * exists, and this page still renders live mode in full for a caller that passes a loader. Trade's
 * SPA has not routed this page since PB-1, so nothing on Trade's side changes today.
 */
type LiveAttributionLoader = (date?: string) => Promise<WeightAttributionResponse>;

export function WeightAttribution({ liveAttribution }: { liveAttribution?: LiveAttributionLoader } = {}) {
  // The modes this render offers, decided by the product context. Lab: backtest only.
  const modes: TabMode[] = liveAttribution ? ['live', 'backtest'] : ['backtest'];
  const liveEnabled = modes.includes('live');
  const [mode, setMode] = useState<TabMode>(modes[0]);
  const [liveData, setLiveData] = useState<WeightAttributionResponse | null>(null);
  const [backtestData, setBacktestData] = useState<BacktestAttributionResponse | null>(null);
  const [selectedDay, setSelectedDay] = useState<BacktestAttributionDay | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [sortField, setSortField] = useState<SortField>('contribution_pct');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [symphonies, setSymphonies] = useState<UserSymphony[]>([]);
  const [selectedSymphony, setSelectedSymphony] = useState('combined');

  useEffect(() => {
    api.getSymphonies().then((res) => {
      setSymphonies(res.symphonies.filter((s) => s.is_active));
    }).catch(() => {});
  }, []);

  const isIndividualSymphony = selectedSymphony !== 'combined';
  const selectedSymphonyObj = isIndividualSymphony
    ? symphonies.find((s) => String(s.id) === selectedSymphony)
    : null;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const loadLiveData = async (dateStr?: string) => {
    // Belt to the type's braces: without a loader there is no live endpoint to call, and calling
    // one Lab does not serve is the whole of bug 1.
    if (!liveAttribution) return;
    setLoading(true);
    setError(null);
    try {
      const result = await liveAttribution(dateStr || selectedDate);
      setLiveData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load attribution data');
    } finally {
      setLoading(false);
    }
  };

  const loadBacktestData = async () => {
    if (startDate >= endDate) {
      setError('Start date must be before end date.');
      return;
    }
    setLoading(true);
    setError(null);
    setBacktestData(null);
    setSelectedDay(null);
    try {
      const symphonyId = selectedSymphony === 'combined' ? undefined : selectedSymphony;
      const result = await api.runBacktestAttribution(startDate, endDate, 100000, symphonyId);
      setBacktestData(result);
      if (result.days.length > 0) {
        setSelectedDay(result.days[result.days.length - 1]);
        setSelectedDayIndex(result.days.length - 1);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run backtest attribution');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (liveEnabled && mode === 'live' && !isIndividualSymphony) {
      loadLiveData();
    }
  }, []);

  const handleModeChange = (newMode: TabMode) => {
    if (newMode === 'live' && (!liveEnabled || isIndividualSymphony)) return;
    setMode(newMode);
    setError(null);
    if (newMode === 'live' && !liveData) {
      loadLiveData();
    }
  };

  const handleSymphonyChange = (value: string) => {
    setSelectedSymphony(value);
    setError(null);
    setBacktestData(null);
    setSelectedDay(null);
    if (value !== 'combined') {
      if (mode === 'live') {
        setMode('backtest');
      }
    } else if (liveEnabled && mode === 'live') {
      loadLiveData();
    }
  };

  const handleDayNav = (direction: 'prev' | 'next') => {
    if (!backtestData) return;
    const newIdx = direction === 'prev' ? selectedDayIndex - 1 : selectedDayIndex + 1;
    if (newIdx >= 0 && newIdx < backtestData.days.length) {
      setSelectedDayIndex(newIdx);
      setSelectedDay(backtestData.days[newIdx]);
    }
  };

  const handleDaySelect = (dateStr: string) => {
    if (!backtestData) return;
    const idx = backtestData.days.findIndex(d => d.date === dateStr);
    if (idx >= 0) {
      setSelectedDayIndex(idx);
      setSelectedDay(backtestData.days[idx]);
    }
  };

  const currentItems = mode === 'live' ? (liveData?.items || []) : (selectedDay?.items || []);
  const hasData = currentItems.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Weight Attribution</h1>
          <p className="text-sm text-gray-500 mt-1">How weight changes translate into daily profit or loss</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <label htmlFor="attribution-strategy" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              <span className="flex items-center gap-1.5">
                <ListFilter className="w-3.5 h-3.5 text-emerald-600" />
                Strategy
              </span>
            </label>
            <select
              id="attribution-strategy"
              value={selectedSymphony}
              onChange={(e) => handleSymphonyChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white text-sm"
              disabled={loading}
            >
              <option value="combined">Combined Strategy (All Allocated)</option>
              {symphonies.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name || s.symphony_id} — {s.source_mode.toUpperCase()}{s.allocation_percent > 0 ? ` (${s.allocation_percent}%)` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* A single-mode product gets a label, not a toggle. A disabled Live button would be a
              control advertising a capability this product does not have — and the toggle is only
              a toggle when there is something to toggle between. */}
          <div className="flex items-center gap-2 self-end">
            {liveEnabled ? (
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => handleModeChange('live')}
                  disabled={isIndividualSymphony}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    mode === 'live' && !isIndividualSymphony
                      ? 'bg-white text-gray-900 shadow-sm'
                      : isIndividualSymphony
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:text-gray-800'
                  }`}
                  title={isIndividualSymphony ? 'Live mode is only available for Combined strategy' : ''}
                >
                  <Radio className="w-4 h-4" />
                  Live
                </button>
                <button
                  onClick={() => handleModeChange('backtest')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    mode === 'backtest' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <FlaskConical className="w-4 h-4" />
                  Backtest
                </button>
              </div>
            ) : (
              <div
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-600"
                title="Attribution here is computed over a historical backtest. Live-position attribution belongs to the trading app."
              >
                <FlaskConical className="w-4 h-4" />
                Backtest attribution
              </div>
            )}
          </div>
        </div>

        {/* The API-mode caveat matters in both products; the sentence about Live mode only makes
            sense where Live mode exists. */}
        {isIndividualSymphony && (liveEnabled || selectedSymphonyObj?.source_mode === 'api') && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <p className="text-blue-700 text-xs">
              {liveEnabled && (
                <>Live mode is not available for individual strategies. Use Backtest mode to analyze attribution for "{selectedSymphonyObj?.name || selectedSymphony}".</>
              )}
              {selectedSymphonyObj?.source_mode === 'api' && (
                <span className="block mt-1 text-amber-700 font-medium">
                  Note: API-mode strategies cannot be backtested historically. Only DSL strategies support backtest attribution.
                </span>
              )}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {mode === 'live' && !isIndividualSymphony && (
            <>
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 px-3 py-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => { setSelectedDate(e.target.value); loadLiveData(e.target.value); }}
                  className="text-sm border-0 outline-none bg-transparent text-gray-700 font-medium"
                />
              </div>
              <button
                onClick={() => loadLiveData()}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </>
          )}

          {mode === 'backtest' && (
            <>
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 px-3 py-2">
                <span className="text-xs text-gray-500">From</span>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  className="text-sm border-0 outline-none bg-transparent text-gray-700 font-medium" />
              </div>
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 px-3 py-2">
                <span className="text-xs text-gray-500">To</span>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  className="text-sm border-0 outline-none bg-transparent text-gray-700 font-medium" />
              </div>
              <button
                onClick={loadBacktestData}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm transition-colors disabled:opacity-50"
              >
                <FlaskConical className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Running...' : 'Run Attribution'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {mode === 'live' && liveData?.message && liveData.items.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-amber-700 text-sm">{liveData.message}</p>
        </div>
      )}

      {mode === 'backtest' && backtestData && (
        <>
          {backtestData.error && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-700 text-sm">{backtestData.error}</p>
            </div>
          )}

          <CollapsibleCard title="Backtest Summary">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Starting Capital</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                ${backtestData.starting_capital.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ending Equity</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                ${backtestData.ending_equity.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Return</p>
              <p className={`text-lg font-bold mt-1 ${colorClass(backtestData.total_return_pct)}`}>
                {formatPct(backtestData.total_return_pct)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Trading Days</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{backtestData.total_trading_days}</p>
              <p className="text-xs text-gray-400 mt-0.5">{backtestData.price_source}</p>
            </div>
          </div>
          </CollapsibleCard>

          {backtestData.days.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Daily Timeline</h3>
                <span className="text-xs text-gray-500">
                  Day {selectedDayIndex + 1} of {backtestData.days.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDayNav('prev')}
                  disabled={selectedDayIndex <= 0}
                  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 overflow-x-auto">
                  <div className="flex gap-1 min-w-0">
                    {backtestData.days.map((day, idx) => (
                      <button
                        key={day.date}
                        onClick={() => handleDaySelect(day.date)}
                        className={`flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                          idx === selectedDayIndex
                            ? 'bg-emerald-700 text-white font-semibold'
                            : day.total_return_pct > 0
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                              : day.total_return_pct < 0
                                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        }`}
                        title={`${day.date}: ${formatPct(day.total_return_pct)}`}
                      >
                        {day.date.slice(5)}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleDayNav('next')}
                  disabled={selectedDayIndex >= backtestData.days.length - 1}
                  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">
            {mode === 'backtest' ? 'Running backtest attribution analysis... This may take a moment.' : 'Loading attribution data...'}
          </p>
        </div>
      ) : hasData ? (
        <>
          {mode === 'live' && liveData && (
            <SummaryCards
              equityBefore={liveData.equity_before}
              totalReturnPct={liveData.total_return_pct}
              totalReturnDollars={liveData.total_return_dollars}
              holdingEffectTotal={liveData.holding_effect_total}
              allocationEffectTotal={liveData.allocation_effect_total}
              assetCount={liveData.items.length}
            />
          )}
          {mode === 'backtest' && selectedDay && (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span className="font-semibold">{selectedDay.date}</span>
                <span className="text-gray-400">|</span>
                <span>Equity: ${selectedDay.equity_before.toLocaleString('en-US', { minimumFractionDigits: 2 })} → ${selectedDay.equity_after.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <SummaryCards
                equityBefore={selectedDay.equity_before}
                totalReturnPct={selectedDay.total_return_pct}
                totalReturnDollars={selectedDay.total_return_dollars}
                holdingEffectTotal={selectedDay.holding_effect_total}
                allocationEffectTotal={selectedDay.allocation_effect_total}
                assetCount={selectedDay.items.length}
              />
            </>
          )}
          <AttributionTable items={currentItems} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
        </>
      ) : (
        !loading && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
            <Scale className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Attribution Data</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              {mode === 'live'
                ? (liveData?.message || 'No rebalance run found for this date. Select a date when a rebalance was executed.')
                : (backtestData?.error || 'Set a date range and click "Run Attribution" to generate backtest attribution data.')
              }
            </p>
          </div>
        )
      )}
    </div>
  );
}
